/**
 * Password Reset Page - Handle password reset from email link
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { FormInput } from '../components/FormInput';
import PageHeader from '../components/PageHeader';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import { useFormValidation } from '../hooks/useFormValidation';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import styles from './PasswordReset.module.css';

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [generalError, setGeneralError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [user_email, setUserEmail] = useState('');
  const [resetComplete, setResetComplete] = useState(false);

  const validationSchema = (values) => {
    const errors = {};
    if (!values.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (values.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }
    if (!values.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (values.newPassword !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    return errors;
  };

  const { formData, errors, touched, handleFieldChange, handleFieldBlur, handleSubmit: handleValidation, isSubmitting } = useFormValidation({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setGeneralError('');
      try {
        const response = await fetch('/api/password-reset/reset-with-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            new_password: values.newPassword
          }),
        });

        if (response.ok) {
          setResetComplete(true);
          return {};
        } else {
          const errorData = await response.json();
          return { general: errorData.detail || 'Failed to reset password' };
        }
      } catch (error) {
        return { general: 'Network error. Please try again.' };
      }
    }
  });

  // Verify token when component mounts
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch('/api/password-reset/verify-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token })
        });

        if (response.ok) {
          const data = await response.json();
          setTokenValid(true);
          setUserEmail(data.email);
        } else {
          const errorData = await response.json();
          setTokenValid(false);
          setGeneralError(errorData.detail || 'Invalid or expired reset link');
        }
      } catch (error) {
        setTokenValid(false);
        setGeneralError('Failed to verify reset link');
      }
    };

    if (!token) {
      setGeneralError('Invalid password reset link');
      return;
    }

    verifyToken();
  }, [token]);

  const handleSubmit = (e) => {
    handleValidation(e);
  };

  const handleGoToLogin = () => {
    navigate('/auth');
  };

  // Show loading state while verifying token
  if (tokenValid === null) {
    return (
      <PageLayout maxWidth="1400px">
        <PageHeader
          title="RESET PASSWORD"
          subtitle="Verifying your reset link..."
        />
        <Section>
          <Card level={1} style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className={styles.centerContent}>
              <div className={styles.loadingIcon}><RefreshCw size={48} className={styles.spin} /></div>
              <p className={styles.loadingText}>
                Please wait while we verify your password reset link...
              </p>
            </div>
          </Card>
        </Section>
      </PageLayout>
    );
  }

  // Show error if token is invalid
  if (tokenValid === false) {
    return (
      <PageLayout maxWidth="1400px">
        <PageHeader
          title="INVALID LINK"
          subtitle="This password reset link is not valid"
        />
        <Section>
          <Card level={1} style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className={styles.centerContent}>
              <div className={styles.errorIcon}><AlertTriangle size={48} /></div>
              <h3 className={styles.errorHeading}>
                Link Invalid or Expired
              </h3>
              <p className={styles.errorMessage}>
                {generalError || 'This password reset link is invalid or has expired.'}
              </p>
              <p className={styles.errorNote}>
                Password reset links expire after 1 hour for security reasons.
              </p>
              <Button variant="primary" onClick={handleGoToLogin} fullWidth>
                Go to Sign In
              </Button>
            </div>
          </Card>
        </Section>
      </PageLayout>
    );
  }

  // Show success message after reset
  if (resetComplete) {
    return (
      <PageLayout maxWidth="1400px">
        <PageHeader
          title="PASSWORD RESET"
          subtitle="Your password has been updated successfully"
        />
        <Section>
          <Card level={1} style={{ maxWidth: '500px', margin: '0 auto' }}>
            <div className={styles.centerContent}>
              <div className={styles.successIcon}><CheckCircle size={48} /></div>
              <h3 className={styles.successHeading}>
                Password Reset Complete!
              </h3>
              <p className={styles.successMessage}>
                Your password has been successfully updated. You can now sign in with your new password.
              </p>
              <Button variant="primary" onClick={handleGoToLogin} fullWidth>
                Sign In Now
              </Button>
            </div>
          </Card>
        </Section>
      </PageLayout>
    );
  }

  // Show password reset form
  return (
    <PageLayout maxWidth="1400px">
      <PageHeader
        title="RESET PASSWORD"
        subtitle={`Create a new password for ${user_email}`}
      />

      <Section>
        <Card level={1} style={{ maxWidth: '500px', margin: '0 auto' }}>
          <form onSubmit={handleSubmit}>
          {generalError && (
            <div className={styles.errorAlert}>
              {generalError}
            </div>
          )}

          <FormInput
            label="New Password"
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.newPassword ? errors.newPassword : ''}
            required
            placeholder="Enter your new password"
            style={{ marginBottom: '1rem' }}
          />

          <FormInput
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            error={touched.confirmPassword ? errors.confirmPassword : ''}
            required
            placeholder="Confirm your new password"
            style={{ marginBottom: '1rem' }}
          />

          <div className={styles.passwordHint}>
            <p className={styles.passwordHintText}>
              Your password must be at least 8 characters long.
            </p>
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting ? 'Updating Password...' : 'Update Password'}
          </Button>

          <div className={styles.backButtonContainer}>
            <Button
              variant="outline"
              theme="light"
              onClick={handleGoToLogin}
              disabled={isSubmitting}
            >
              Back to Sign In
            </Button>
          </div>
        </form>
        </Card>
      </Section>
    </PageLayout>
  );
};

export default PasswordReset;
